import type { ReactNode } from "react"
import { createContext, useContext } from "react"

import type { Language } from "../../settings/settings-pages/language-selector"
import { useSelectedLanguage } from "../../settings/settings-pages/language-selector"

interface HelpCenterContextValue {
	selectedLanguage: Language
	setSelectedLanguage: (language: Language) => void
	onItemSelected: () => void
	articlesSettled: boolean
}

const HelpCenterContext = createContext<HelpCenterContextValue | null>(null)

interface HelpCenterProviderProps {
	children: ReactNode
	onItemSelected: () => void
	articlesSettled: boolean
}

export function HelpCenterProvider({
	children,
	onItemSelected,
	articlesSettled,
}: HelpCenterProviderProps) {
	const [selectedLanguage, setSelectedLanguage] = useSelectedLanguage()

	const value: HelpCenterContextValue = {
		selectedLanguage,
		setSelectedLanguage,
		onItemSelected,
		articlesSettled,
	}

	return (
		<HelpCenterContext.Provider value={value}>
			{children}
		</HelpCenterContext.Provider>
	)
}

export function useHelpCenterLanguage() {
	const context = useContext(HelpCenterContext)
	if (!context) {
		throw new Error(
			"useHelpCenterLanguage must be used within a HelpCenterProvider",
		)
	}
	return {
		selectedLanguage: context.selectedLanguage,
		setSelectedLanguage: context.setSelectedLanguage,
	}
}

export function useHelpCenterNav() {
	const context = useContext(HelpCenterContext)
	if (!context) {
		throw new Error("useHelpCenterNav must be used within a HelpCenterProvider")
	}

	return {
		onItemSelected: context.onItemSelected,
		selectedLanguage: context.selectedLanguage,
	}
}

// Zero streams rows in before the article query is authoritative, so the
// dashed add tiles would flash on a workspace that does have articles.
export function useHelpCenterArticlesSettled() {
	const context = useContext(HelpCenterContext)
	if (!context) {
		throw new Error(
			"useHelpCenterArticlesSettled must be used within a HelpCenterProvider",
		)
	}
	return context.articlesSettled
}
